import { useState, useEffect } from 'react';

interface LocationData {
  country: string;
  countryCode: string;
  region: string;
  city: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

interface LocationDetectionResult {
  location: LocationData | null;
  isLoading: boolean;
  error: string | null;
  detectLocation: () => Promise<void>;
}

export const useLocationDetection = (): LocationDetectionResult => {
  const [location, setLocation] = useState<LocationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get location from browser locale as fallback
  const getBrowserLocation = (): Partial<LocationData> => {
    const locale = navigator.language || 'en-US';
    const countryCode = locale.split('-')[1] || 'US';
    
    // Map common country codes to country names
    const countryMap: Record<string, string> = {
      'US': 'United States',
      'IN': 'India',
      'GB': 'United Kingdom',
      'CA': 'Canada',
      'AU': 'Australia',
      'DE': 'Germany',
      'FR': 'France',
      'JP': 'Japan',
      'CN': 'China',
      'BR': 'Brazil',
      'MX': 'Mexico',
      'IT': 'Italy',
      'ES': 'Spain',
      'RU': 'Russia',
      'KR': 'South Korea',
      'NL': 'Netherlands',
      'SE': 'Sweden',
      'NO': 'Norway',
      'DK': 'Denmark',
      'FI': 'Finland'
    };

    return {
      countryCode,
      country: countryMap[countryCode] || countryCode,
      region: '',
      city: ''
    };
  };

  // Get location from IP geolocation service
  const getIPLocation = async (): Promise<LocationData> => {
    try {
      // Using ipapi.co as it's free and reliable
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) {
        throw new Error('Failed to fetch IP location');
      }
      
      const data = await response.json();
      
      return {
        country: data.country_name || 'Unknown',
        countryCode: data.country_code || 'US',
        region: data.region || '',
        city: data.city || '',
        coordinates: data.latitude && data.longitude ? {
          lat: data.latitude,
          lng: data.longitude
        } : undefined
      };
    } catch (error) {
      console.error('IP location detection failed:', error);
      throw error;
    }
  };

  // Get location from GPS
  const getGPSLocation = async (): Promise<Partial<LocationData>> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Reverse geocoding using a free service
            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            
            if (!response.ok) {
              throw new Error('Reverse geocoding failed');
            }
            
            const data = await response.json();
            
            resolve({
              country: data.countryName || 'Unknown',
              countryCode: data.countryCode || 'US',
              region: data.principalSubdivision || '',
              city: data.city || data.locality || '',
              coordinates: { lat: latitude, lng: longitude }
            });
          } catch (error) {
            console.error('Reverse geocoding failed:', error);
            resolve({
              coordinates: { lat: position.coords.latitude, lng: position.coords.longitude }
            });
          }
        },
        (error) => {
          console.error('GPS location failed:', error);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  };

  const detectLocation = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      let detectedLocation: LocationData;

      // Try GPS first if available
      try {
        const gpsLocation = await getGPSLocation();
        if (gpsLocation.country && gpsLocation.countryCode) {
          detectedLocation = gpsLocation as LocationData;
        } else {
          // GPS didn't provide country info, try IP location
          const ipLocation = await getIPLocation();
          detectedLocation = {
            ...ipLocation,
            coordinates: gpsLocation.coordinates || ipLocation.coordinates
          };
        }
      } catch (gpsError) {
        // GPS failed, try IP location
        try {
          detectedLocation = await getIPLocation();
        } catch (ipError) {
          // Both failed, use browser locale
          const browserLocation = getBrowserLocation();
          detectedLocation = {
            country: browserLocation.country || 'Unknown',
            countryCode: browserLocation.countryCode || 'US',
            region: browserLocation.region || '',
            city: browserLocation.city || ''
          };
        }
      }

      setLocation(detectedLocation);
      
      // Cache the location for future use
      localStorage.setItem('userLocation', JSON.stringify(detectedLocation));
      
    } catch (error: any) {
      console.error('Location detection failed:', error);
      setError(error.message || 'Failed to detect location');
      
      // Use cached location if available
      const cached = localStorage.getItem('userLocation');
      if (cached) {
        try {
          setLocation(JSON.parse(cached));
        } catch (parseError) {
          console.error('Failed to parse cached location:', parseError);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Load cached location on mount
  useEffect(() => {
    const cached = localStorage.getItem('userLocation');
    if (cached) {
      try {
        const cachedLocation = JSON.parse(cached);
        setLocation(cachedLocation);
      } catch (error) {
        console.error('Failed to parse cached location:', error);
      }
    }
  }, []);

  return {
    location,
    isLoading,
    error,
    detectLocation
  };
};